// MB-1461 — pure state machine for the in-wizard caption review wait
// ("review captions before rendering").
//
// The wizard stays open after submit: waiting (transcription ~1-2 min) →
// ready (transcript editor) → approving → done (normal completion modal).
// Inputs interleave from the submit response, the zapcap-recaption socket
// channel and the GET /post/zapcap-review poll fallback, so every transition
// is guarded: duplicate/stale events never regress the phase and terminal
// phases are one-way. There is deliberately NO event that approves — only
// the user's explicit approve request moves ready → approving (RULED: never
// auto-approve).

export type ReviewPhase =
    | "idle"
    | "waiting"
    | "ready"
    | "approving"
    | "done"
    | "failed";

export type ReviewFailureReason =
    | "abandoned"
    | "captioning-failed"
    | "error";

export interface ReviewWaitState {
    phase: ReviewPhase;
    taskId: string | null;
    failureReason: ReviewFailureReason | null;
    // The transcription found no spoken words: "ready" with nothing to edit.
    // The wizard offers "continue without captions" (approve) or cancel.
    noSpeech: boolean;
}

export type ReviewPollState =
    | "waiting"
    | "transcript-ready"
    | "no-speech"
    | "approved"
    | "abandoned"
    | "failed";

export type ReviewWaitEvent =
    | { type: "submitted"; taskId: string }
    | { type: "transcript-ready" }
    | { type: "socket-failed"; reason?: string }
    | { type: "poll"; taskId: string; state: ReviewPollState }
    | { type: "approve-started" }
    | { type: "approve-succeeded" }
    | { type: "approve-failed"; gone?: boolean }
    | { type: "reset" };

export const initialReviewWaitState: ReviewWaitState = {
    phase: "idle",
    taskId: null,
    failureReason: null,
    noSpeech: false,
};

export function mapReviewFailureReason(
    reason: string | undefined
): ReviewFailureReason {
    if (reason === "review-abandoned") return "abandoned";
    if (reason === "review-cancelled") return "abandoned";
    if (reason === "captioning-failed") return "captioning-failed";
    return "error";
}

const LIVE_PHASES: ReviewPhase[] = ["waiting", "ready", "approving"];

// True while leaving the wizard would abandon the captioned video (the
// server-side reaper deletes the held draft posts).
export function shouldWarnOnLeave(state: ReviewWaitState): boolean {
    return LIVE_PHASES.includes(state.phase);
}

export function reviewWaitReducer(
    state: ReviewWaitState,
    event: ReviewWaitEvent
): ReviewWaitState {
    switch (event.type) {
        case "reset":
            return initialReviewWaitState;

        case "submitted":
            if (state.phase !== "idle") return state;
            return {
                phase: "waiting",
                taskId: event.taskId,
                failureReason: null,
                noSpeech: false,
            };

        case "transcript-ready":
            if (state.phase !== "waiting") return state;
            return { ...state, phase: "ready", noSpeech: false };

        case "socket-failed":
            if (!LIVE_PHASES.includes(state.phase)) return state;
            return {
                ...state,
                phase: "failed",
                failureReason: mapReviewFailureReason(event.reason),
            };

        case "poll":
            if (!LIVE_PHASES.includes(state.phase)) return state;
            // A response still in flight from a previous review must never
            // settle the one that replaced it.
            if (event.taskId !== state.taskId) return state;
            switch (event.state) {
                case "transcript-ready":
                    return state.phase === "waiting"
                        ? { ...state, phase: "ready", noSpeech: false }
                        : state;
                case "no-speech":
                    // Nothing to edit; the user still decides (continue or
                    // cancel) — the reducer never approves on its own.
                    return state.phase === "waiting"
                        ? { ...state, phase: "ready", noSpeech: true }
                        : state;
                case "approved":
                    // Approved elsewhere (another tab) — the render is on its
                    // way; show the normal completion modal.
                    return state.phase === "approving"
                        ? state
                        : { ...state, phase: "done" };
                case "abandoned":
                    return {
                        ...state,
                        phase: "failed",
                        failureReason: "abandoned",
                    };
                case "failed":
                    return {
                        ...state,
                        phase: "failed",
                        failureReason: "captioning-failed",
                    };
                case "waiting":
                default:
                    return state;
            }

        case "approve-started":
            if (state.phase !== "ready") return state;
            return { ...state, phase: "approving" };

        case "approve-succeeded":
            if (state.phase !== "approving") return state;
            return { ...state, phase: "done" };

        case "approve-failed":
            if (state.phase !== "approving") return state;
            if (event.gone) {
                return { ...state, phase: "failed", failureReason: "abandoned" };
            }
            return { ...state, phase: "ready" };

        default:
            return state;
    }
}
